import React, { useRef, useEffect, useState } from 'react';

import CommentCard from './CommentCard';
import FontAwesomeIcon from '../../common/FontAwesomeIcon';
import CommonButton from '../../common/CommonButton';
import EditCommentModal from './EditCommentModal';

import PickRecAudio from '../../../pages/speedgrader/components/studentTask/commentsSection/PickRecAudio';
import PickRecVideo from '../../../pages/speedgrader/components/studentTask/commentsSection/PickRecVideo';
import AttachmentsFile from '../../../pages/speedgrader/components/studentTask/commentsSection/AttachmentsFile';

import { IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';

import styled from 'styled-components';

const CommentsChat = (props) => {
    const {
        comments,
        imagePreview,
        handleRemoveImage,
        handleImageChange,
        handleSendMessage,
        comment,
        setComment,
        isLoading,
        selectedImage,
        disabledWhenEmpty,
        emptyConversationMessage,
        hideComposer = false,
        inputPlaceholder,
        sendButtonLabel,
        canEditComment,
        onEditMessage,
        isEditingMessage = false,
        composerVariant = 'image',
        handleSendAudio,
        handleSendVideo,
        handleSendFile,
        mediaPickerResetKey = 0,
        fileInputId = 'presentation-comments-file',
    } = props;

    const commentsEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const chatTextAreaRef = useRef(null);
    const fileInputRef = useRef(null);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [editingFile, setEditingFile] = useState(null);
    const [editingAudio, setEditingAudio] = useState(null);
    const [editingVideo, setEditingVideo] = useState(null);
    const [editingModalMediaKey, setEditingModalMediaKey] = useState(0);
    const isDisabled = disabledWhenEmpty && comments.length === 0;
    const editingMessage =
        comments.find((item) => item.id === editingMessageId) || null;
    const originalEditingValue = editingMessage?.comment || '';
    const isMediaComposer = composerVariant === 'media';

    const scrollToBottom = (behavior = 'smooth') => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop =
                messagesContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        if (comments.length > 0) {
            const timeout = setTimeout(() => {
                scrollToBottom('auto');
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [comments.length]);

    const autoResizeChat = () => {
        const textArea = chatTextAreaRef.current;
        if (!textArea) return;
        textArea.style.height = 'auto';
        textArea.style.height = `${textArea.scrollHeight}px`;
    };

    useEffect(() => {
        autoResizeChat();
    }, [comment]);

    const handleOpenEditModal = (currentComment) => {
        setEditingMessageId(currentComment.id);
        setEditingValue(currentComment.comment);
        setEditingFile(null);
        setEditingAudio(null);
        setEditingVideo(null);
        setEditingModalMediaKey((k) => k + 1);
    };

    const handleCloseEditModal = () => {
        if (isEditingMessage) return;
        setEditingMessageId(null);
        setEditingValue('');
        setEditingFile(null);
        setEditingAudio(null);
        setEditingVideo(null);
    };

    const handleSaveEditedMessage = async () => {
        if (!editingMessage || !onEditMessage) return;

        const didSave = await onEditMessage(editingMessage.id, editingValue, {
            file: editingFile,
            audio: editingAudio,
            video: editingVideo,
        });

        if (didSave) {
            handleCloseEditModal();
        }
    };

    const onRemoveImageComposer = () => {
        handleRemoveImage();
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const sendDisabledResolved = isLoading || isDisabled || !comment.trim();

    return (
        <ChatWrapper>
            <MessagesContainer ref={messagesContainerRef}>
                {comments.map((commentItem) => (
                    <CommentCard
                        key={commentItem.id}
                        comment={commentItem}
                        canEdit={
                            !!canEditComment && canEditComment(commentItem)
                        }
                        onEdit={handleOpenEditModal}
                    />
                ))}
                <div ref={commentsEndRef} />
            </MessagesContainer>

            {!hideComposer && (
                <FooterContainer>
                    {isDisabled && emptyConversationMessage && (
                        <EmptyMessageText>
                            {emptyConversationMessage}
                        </EmptyMessageText>
                    )}
                    {!isMediaComposer && imagePreview && (
                        <ImagePreviewContainer>
                            <PreviewImage src={imagePreview} alt="Preview" />
                            <RemoveImageButton onClick={onRemoveImageComposer}>
                                <Close />
                            </RemoveImageButton>
                        </ImagePreviewContainer>
                    )}
                    <FieldOuter>
                        <FloatingLabel>Comentario</FloatingLabel>
                        <TextAreaWrapper $disabled={isDisabled}>
                            <CommentTextArea
                                ref={chatTextAreaRef}
                                placeholder={inputPlaceholder}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={4}
                                disabled={isLoading}
                                readOnly={isDisabled}
                                aria-disabled={isDisabled}
                                onInput={autoResizeChat}
                            />
                            <ToolbarSeparator />
                            <ToolbarActions $media={isMediaComposer}>
                                {isMediaComposer ? (
                                    <ContentAttachments>
                                        <PickRecAudio
                                            key={`ca-${mediaPickerResetKey}`}
                                            handleSendAudio={handleSendAudio}
                                        />
                                        <PickRecVideo
                                            key={`cv-${mediaPickerResetKey}`}
                                            handleSendVideo={handleSendVideo}
                                        />
                                        <AttachmentsFile
                                            key={`cf-${mediaPickerResetKey}`}
                                            handleSendFile={handleSendFile}
                                            fileInputId={fileInputId}
                                        />
                                    </ContentAttachments>
                                ) : (
                                    <ImageUploadButton $disabled={isDisabled}>
                                        <input
                                            ref={fileInputRef}
                                            id="image-upload-input"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            onClick={(e) => {
                                                e.currentTarget.value = '';
                                            }}
                                            style={{ display: 'none' }}
                                            disabled={isDisabled}
                                        />
                                        <label htmlFor="image-upload-input">
                                            <FontAwesomeIcon icon="fa-solid fa-image" />
                                        </label>
                                    </ImageUploadButton>
                                )}
                                <SendButtonWrapper>
                                    <CommonButton
                                        variant="outlined"
                                        label={sendButtonLabel}
                                        type="button"
                                        onClick={handleSendMessage}
                                        disabled={sendDisabledResolved}
                                    />
                                </SendButtonWrapper>
                            </ToolbarActions>
                        </TextAreaWrapper>
                    </FieldOuter>
                </FooterContainer>
            )}

            <EditCommentModal
                open={!!editingMessage}
                value={editingValue}
                originalValue={originalEditingValue}
                attachmentUrls={{
                    file:
                        editingMessage?.attachments?.fileUrl ||
                        editingMessage?.image ||
                        null,
                    audio: editingMessage?.attachments?.audioUrl || null,
                    video: editingMessage?.attachments?.videoUrl || null,
                }}
                selectedFile={editingFile}
                selectedAudio={editingAudio}
                selectedVideo={editingVideo}
                isLoading={isEditingMessage}
                onChange={setEditingValue}
                onFileChange={setEditingFile}
                onAudioChange={setEditingAudio}
                onVideoChange={setEditingVideo}
                onClose={handleCloseEditModal}
                onSubmit={handleSaveEditedMessage}
                mediaPickerResetKey={editingModalMediaKey}
            />
        </ChatWrapper>
    );
};

export default CommentsChat;

const ChatWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 200px;
    min-width: 0;
    max-width: 100%;
`;

const MessagesContainer = styled.div`
    flex: 1;
    min-width: 0;
    padding-bottom: 0;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    min-height: 0;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
        background: #cccccc;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #b0b0b0;
    }
`;

const FooterContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    background: transparent;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    flex-shrink: 0;
`;

const EmptyMessageText = styled.p`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 2rem 0;
    color: #616161;
    font-size: 16px;
    font-weight: 500;
`;

const FieldOuter = styled.div`
    position: relative;
    width: 100%;
    padding-top: 10px;
`;

const TextAreaWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #616161;
    border-radius: 18px;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
    overflow: clip;
`;

const FloatingLabel = styled.span`
    position: absolute;
    top: 10px;
    left: 14px;
    transform: translateY(-50%);
    z-index: 1;
    padding: 0 6px;
    background: white;
    color: #616161;
    font-size: 0.85rem;
    font-weight: 500;
    line-height: 1.2;
    pointer-events: none;
`;

const CommentTextArea = styled.textarea`
    width: 100%;
    padding: 20px 14px 12px;
    border: none;
    background: transparent;
    font-size: 16px;
    resize: vertical;
    overflow: hidden;
    min-height: 160px;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;

    &::placeholder {
        color: #999;
    }

    &::-webkit-resizer {
        border-width: 8px;
        border-style: solid;
        border-color: transparent #616161 #616161 transparent;
    }
`;

const ToolbarActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${(p) => (p.$media ? 'space-between' : 'space-between')};
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 12px;
    background: #f8f9fa;
    flex-shrink: 0;
    min-height: 48px;
`;

const ToolbarSeparator = styled.div`
    width: 100%;
    height: 1px;
    background: #e0e0e0;
    margin: 0;
`;

const ContentAttachments = styled.div`
    display: flex;
    gap: 0.25rem;
    align-items: center;
    flex-wrap: wrap;
    min-width: 0;
`;

const ImageUploadButton = styled.div`
    label {
        cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px;
        border-radius: 50%;
        background: transparent;
        color: #616161;
        transition: all 0.2s;
        opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
        pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
        &:hover {
            background: #e0e0e0;
            color: #b31d15;
        }
        svg {
            font-size: 16px;
        }
    }
`;

const SendButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
`;

const ImagePreviewContainer = styled.div`
    position: relative;
    width: 60px;
    height: 60px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #ddd;
`;

const PreviewImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const RemoveImageButton = styled(IconButton)`
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 4px;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }

    svg {
        font-size: 16px;
    }
`;
